// src/components/TruckDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaFileAlt, FaCalendarAlt } from "react-icons/fa";
import axios from "../axiosConfig";
import "../styles/TruckDetails.css";
import { useTranslation } from "react-i18next";

/* ===== Helpers de URL/ficheiro ===== */
const API_BASE = (axios?.defaults?.baseURL || "").replace(/\/+$/, "");
const buildFileUrl = (p) => {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const clean = ("/" + p).replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  return `${API_BASE}${clean}`;
};
const fileName = (p) => {
  if (!p) return "";
  const clean = String(p).replace(/\\/g, "/");
  return clean.split("/").pop();
};

function TruckDetails() {
  const { t } = useTranslation("truck_details");
  const { id } = useParams();
  const navigate = useNavigate();
  const [truck, setTruck] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const monthLabel = (m) => (m ? t(`months.${m}`) : "");

  const fetchTruckDetails = async () => {
    if (!id) {
      setErrorMessage(t("errors.invalid_id"));
      return;
    }
    try {
      const response = await axios.get(`/trucks/${id}`);
      setTruck(response.data);
    } catch (error) {
      setErrorMessage(t("errors.fetch_failed"));
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`/trucks/${truck.id}`, { status: newStatus });
      alert(t("status.update_success"));
      fetchTruckDetails();
    } catch (error) {
      console.error("Error updating truck status:", error.message);
      alert(t("status.update_failed"));
    }
  };

  useEffect(() => {
    fetchTruckDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!truck) return <div className="truck-details-loading-message">{t("loading")}</div>;

  // URLs normalizados
  const inspectionPath = truck.inspectionPhoto || truck.InspectionPhoto || null;
  const vrcPath = truck.vehicleRegistrationCertificate || truck.VehicleRegistrationCertificate || null;
  const communityPath = truck.communityLicence || truck.CommunityLicence || null;

  // Expiry dates normalizados (aceita camelCase, PascalCase e snake_case)
  const inspectionExpiry =
    truck.InspectionPhotoExpiry ||
    truck.inspectionPhotoExpiry ||
    truck.inspection_photo_expiry ||
    null;

  const vrcExpiry =
    truck.VehicleRegistrationCertificateExpiry ||
    truck.vehicleRegistrationCertificateExpiry ||
    truck.vehicle_registration_certificate_expiry ||
    null;

  const communityExpiry =
    truck.CommunityLicenceExpiry ||
    truck.communityLicenceExpiry ||
    truck.community_licence_expiry ||
    null;

  return (
    <div className="truck-details-container">
      <div className="truck-details-back-arrow" onClick={() => navigate("/trucks")}>
        ← {t("back")}
      </div>

      <h1 className="truck-details-title">{t("title")}</h1>
      {errorMessage && <p className="truck-details-error-message">{errorMessage}</p>}

      <form className="truck-details-form">
        {/* Row 1: VIN and Type of Gas */}
        <div className="truck-details-row">
          <div className="truck-details-item">
            <label>{t("labels.vin")}</label>
            <input type="text" readOnly value={truck.vin || t("na")} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.type_of_gas")}</label>
            <input type="text" readOnly value={truck.type_of_gas || t("na")} />
          </div>
        </div>

        {/* Row 2: Brand, Model, Plate */}
        <div className="truck-details-row">
          <div className="truck-details-item">
            <label>{t("labels.brand")}</label>
            <input type="text" readOnly value={truck.brand || t("na")} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.model")}</label>
            <input type="text" readOnly value={truck.model || t("na")} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.plate")}</label>
            <input type="text" readOnly value={truck.plate || t("na")} />
          </div>
        </div>

        {/* Row 3: Month and Year */}
        <div className="truck-details-row">
          <div className="truck-details-item">
            <label>{t("labels.month")}</label>
            <input
              type="text"
              readOnly
              value={
                monthLabel(Number(truck.Month) || Number(truck.month) || 0) || t("na")
              }
            />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.year")}</label>
            <input type="text" readOnly value={truck.year || t("na")} />
          </div>
        </div>

        {/* Row 4: Equipment counts */}
        <div className="truck-details-row">
          <div className="truck-details-item">
            <label>{t("labels.number_of_straps")}</label>
            <input type="text" readOnly value={truck.number_of_straps ?? "0"} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.number_of_ratchets")}</label>
            <input type="text" readOnly value={truck.number_of_ratchets ?? "0"} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.number_of_edge_protectors")}</label>
            <input type="text" readOnly value={truck.number_of_edge_protectors ?? "0"} />
          </div>
          <div className="truck-details-item">
            <label>{t("labels.number_of_anti_slip_mats")}</label>
            <input type="text" readOnly value={truck.number_of_anti_slip_mats ?? "0"} />
          </div>
        </div>

        {/* Row 5: Files + Expiry Dates */}
        <div className="truck-details-file-row">
          {/* Inspection Photo */}
          <div className="truck-details-file-group">
            <label className="truck-details-label">{t("labels.inspection_photo")}</label>
            <div className="truck-details-upload-box">
              {inspectionPath ? (
                <div
                  className="truck-details-file-loaded"
                  onClick={() => window.open(buildFileUrl(inspectionPath), "_blank")}
                  style={{ cursor: "pointer", width: "100%" }}
                >
                  <FaFileAlt className="truck-details-file-icon" />
                  <span className="truck-details-file-name">{fileName(inspectionPath)}</span>
                </div>
              ) : (
                <div className="truck-details-upload-placeholder">
                  <p className="truck-details-upload-text">{t("file.no_document_uploaded")}</p>
                </div>
              )}
            </div>
            <label className="truck-details-label">{t("labels.expiry_date")}</label>
            <div className="truck-details-date-wrapper">
            <input
                type="text"
                readOnly
                value={
                  inspectionExpiry
                    ? new Date(inspectionExpiry).toLocaleDateString()
                    : t("na")
                }
                className="truck-details-date-input"
              />
              <FaCalendarAlt className="truck-details-calendar-icon" />
            </div>
          </div>

          {/* Vehicle Registration Certificate */}
          <div className="truck-details-file-group">
            <label className="truck-details-label">{t("labels.vehicle_registration_certificate")}</label>
            <div className="truck-details-upload-box">
              {vrcPath ? (
                <div
                  className="truck-details-file-loaded"
                  onClick={() => window.open(buildFileUrl(vrcPath), "_blank")}
                  style={{ cursor: "pointer", width: "100%" }}
                >
                  <FaFileAlt className="truck-details-file-icon" />
                  <span className="truck-details-file-name">{fileName(vrcPath)}</span>
                </div>
              ) : (
                <div className="truck-details-upload-placeholder">
                  <p className="truck-details-upload-text">{t("file.no_document_uploaded")}</p>
                </div>
              )}
            </div>
            <label className="truck-details-label">{t("labels.expiry_date")}</label>
            <div className="truck-details-date-wrapper">
            <input
                type="text"
                readOnly
                value={
                  vrcExpiry
                    ? new Date(vrcExpiry).toLocaleDateString()
                    : t("na")
                }
                className="truck-details-date-input"
              />
              <FaCalendarAlt className="truck-details-calendar-icon" />
            </div>
          </div>

          {/* Community Licence */}
          <div className="truck-details-file-group">
            <label className="truck-details-label">{t("labels.community_licence")}</label>
            <div className="truck-details-upload-box">
              {communityPath ? (
                <div
                  className="truck-details-file-loaded"
                  onClick={() => window.open(buildFileUrl(communityPath), "_blank")}
                  style={{ cursor: "pointer", width: "100%" }}
                >
                  <FaFileAlt className="truck-details-file-icon" />
                  <span className="truck-details-file-name">{fileName(communityPath)}</span>
                </div>
              ) : (
                <div className="truck-details-upload-placeholder">
                  <p className="truck-details-upload-text">{t("file.no_document_uploaded")}</p>
                </div>
              )}
            </div>
            <label className="truck-details-label">{t("labels.expiry_date")}</label>
            <div className="truck-details-date-wrapper">
            <input
                type="text"
                readOnly
                value={
                  communityExpiry
                    ? new Date(communityExpiry).toLocaleDateString()
                    : t("na")
                }
                className="truck-details-date-input"
              />
              <FaCalendarAlt className="truck-details-calendar-icon" />
            </div>
          </div>
        </div>

        <div className="truck-details-edit-button-container">
          <button
            type="button"
            className="truck-details-edit-btn"
            onClick={() =>
              navigate(`/trucks/${id}/edit`, {
                state: {
                  prefill: {
                    ...truck,
                    month: Number(truck.Month) || truck.month || "",
                    InspectionPhoto: inspectionPath || null,
                    VehicleRegistrationCertificate: vrcPath || null,
                    CommunityLicence: communityPath || null,
                    InspectionPhotoExpiry: truck.InspectionPhotoExpiry || null,
                    VehicleRegistrationCertificateExpiry: truck.VehicleRegistrationCertificateExpiry || null,
                    CommunityLicenceExpiry: truck.CommunityLicenceExpiry || null,
                  },
                },
              })
            }
          >
            {t("buttons.edit")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TruckDetails;
